<template>
  <div v-if="user.subscription_limit">
    <v-card>
      <v-card-title class="d-block">
        <helpers-section-sub-header class="mb-2"> Manage team members </helpers-section-sub-header>
        <div style="max-width: 800px">
          Team members automatically gain access to all of your account subscription benefits.
        </div>
      </v-card-title>
      <v-card-text>
        <!-- Add team member -->
        <v-btn
          :disabled="loading"
          color="primary"
          class="mx-0 mb-6"
          @click="addTeamMemberDialog = true"
        >
          Add team member
        </v-btn>

        <!-- Max team members allowed note -->
        <div class="my-2">
          <v-alert
            :value="true"
            :icon="mdiAlertCircle"
            color="white text-secondary"
            class="d-inline-flex text-body-2 mt-0 mb-2"
          >
            Your subscription allows up to {{ user.subscription_limit.allowed_team_members }} team
            members.
          </v-alert>
        </div>

        <!-- Members table -->
        <v-data-table
          :headers="headers"
          :items="user.shared_accounts"
          :loading="loading"
          no-data-text="No existing team members."
          hide-default-footer
          items-per-page="-1"
        >
          <template #item="{ item }">
            <tr>
              <td>
                {{ item.secondary_user.email }}
              </td>
              <td class="pl-12">
                <v-btn
                  small
                  text
                  :icon="mdiBackspace"
                  color="error"
                  title="Revoke membership"
                  class="ma-0"
                  @click="revokeMembership(item)"
                />
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dialog: Add team member -->
    <v-dialog v-model="addTeamMemberDialog" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <v-toolbar-title>Add New Team Member</v-toolbar-title>
        </v-card-title>
        <v-card-text>
          <v-form v-model="validEmail" @submit.prevent>
            <v-text-field
              v-model="email"
              :rules="[rules.required, rules.email]"
              label="E-mail"
              hint="* User must already have an account on WhaleWisdom"
              persistent-hint
              autocorrect="off"
              spellcheck="false"
            />
            <div v-show="inviteError" class="v-messages text-error mt-2">
              {{ inviteError }}
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-4">
          <v-spacer />
          <v-btn text @click="addTeamMemberDialog = false"> Cancel </v-btn>
          <v-btn
            :loading="inviting"
            :disabled="!validEmail || inviting"
            color="primary"
            @click="addTeamMember()"
          >
            Add
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Revoke membership -->
    <v-dialog v-model="revokeMembershipDialog" persistent max-width="400px">
      <v-card v-if="memberToRevoke">
        <v-card-title>
          <v-toolbar-title>Revoke Membership</v-toolbar-title>
        </v-card-title>
        <v-card-text>
          Are you sure you want to revoke {{ memberToRevoke.secondary_user.email }}'s team
          membership?
          <v-alert v-if="revokeError" error>
            {{ revokeError }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="px-4">
          <v-spacer />
          <v-btn variant="text" @click="revokeMembershipDialog = false"> Cancel </v-btn>
          <v-btn :loading="revoking" :disabled="revoking" color="error" @click="revokeConfirmed()">
            Revoke membership
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Modules
import api from '@/store/api'
import { mapStoreState } from '@/store/helpers'
import { mdiAlertCircle, mdiBackspace } from '@mdi/js'

export default {
  data() {
    return {
      mdiAlertCircle,
      mdiBackspace,
      addTeamMemberDialog: false,
      revokeMembershipDialog: false,
      validEmail: false,
      loading: false,
      inviting: false,
      revoking: false,
      email: null,
      inviteError: null,
      memberToRevoke: null,
      revokeError: null,
      headers: [
        { title: 'Email Address', key: 'email' },
        { title: '', key: 'action' }
      ],
      rules: {
        required: value => !!value || 'This field is required',
        email: value => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || 'Invalid e-mail'
        }
      }
    }
  },

  computed: {
    ...mapStoreState({
      user: state => state.user.user
    })
  },

  methods: {
    addTeamMember() {
      this.inviteError = null
      this.inviting = true

      api
        .get(`add_team_member?email=${this.email}`)
        .then(response => {
          useUserStore().loadUser()
          this.email = null
          this.addTeamMemberDialog = false
        })
        .catch(error => {
          this.inviteError = error.response.data[0] || error.response
        })
        .then(() => (this.inviting = false))
    },

    revokeMembership(member) {
      this.memberToRevoke = member
      this.revokeMembershipDialog = true
    },

    revokeConfirmed() {
      this.revokeError = null
      this.revoking = true

      api
        .get(`revoke_team_member?email=${this.memberToRevoke.secondary_user.email}`)
        .then(response => {
          useUserStore().loadUser()
          this.revokeMembershipDialog = false
          this.memberToRevoke = null
        })
        .catch(error => {
          this.revokeError = error.response.data[0] || error.response
          this.memberToRevoke = null
        })
        .then(() => (this.revoking = false))
    }
  }
}
</script>

<style lang="scss" scoped>
table {
  display: inline-block;
}
</style>
